import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PiCheckCircleLight, PiPaperPlaneTiltLight, PiSignatureLight } from 'react-icons/pi';
import { useAppDispatch } from '../../store';
import { showModal } from '../../store/actions/modalActions';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import GlassCard from '../shared/brand/GlassCard';
import { Well } from '../shared/brand/Field';
import Button from '../shared/TailwindComponents/Button';
import Input from '../shared/TailwindComponents/Input';
import Textarea from '../shared/TailwindComponents/Textarea';
import Skeleton from '../shared/TailwindComponents/Skeleton';
import {
  EngagementLetter as Letter,
  EngagementRole,
  SIGNING_LEAD_KEY,
  confirmLetter,
  startLetterSigning,
  errorMessage,
  getLetter,
  issueLetter,
  saveLetter,
} from '../../services/api/marketplaceService';
import { fmtDate } from './marketplaceUi';

/** Mirrors letters.PLACEHOLDER on the backend. */
const PLACEHOLDER = /\[(?:Fyll inn|Completar)[^\]]*\]/g;
const MAX_LETTER = 20000;

/**
 * The engagement letter. Lawyer: a draft made from the agreed terms, in their
 * licence country's language; they fill in the marked parts, then issue it (it
 * goes into the matter and to the client). Client: read and confirm it.
 */
const EngagementLetter: React.FC<{
  leadUuid: string;
  as: EngagementRole;
  /** The issued letter is now a shared document; lets the page refresh that list. */
  onIssued?: () => void;
}> = ({ leadUuid, as, onIssued }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [letter, setLetter] = useState<Letter | null | undefined>(undefined);
  const [text, setText] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState<'save' | 'issue' | 'confirm' | 'sign' | null>(null);

  const toast = useCallback((modalType: 'success' | 'error', message: string) =>
    dispatch(showFeedbackModal({ showModal: true, modalType, duration: 4000, message })), [dispatch]);

  useEffect(() => {
    getLetter(leadUuid, as)
      .then((l) => { setLetter(l); setText(l.text ?? ''); })
      .catch(() => setLetter(null));
  }, [leadUuid, as]);

  const dirty = letter?.status === 'draft' && text !== (letter.text ?? '');
  const missing = (text.match(PLACEHOLDER) ?? []).length;
  const tooLong = text.length > MAX_LETTER;

  const save = async (): Promise<boolean> => {
    setBusy('save');
    try {
      const l = await saveLetter(leadUuid, text);
      setLetter(l);
      setText(l.text ?? '');
      return true;
    } catch (err) {
      toast('error', errorMessage(err, t('Could not save the letter')));
      return false;
    } finally {
      setBusy(null);
    }
  };

  const issue = async () => {
    if (dirty && !(await save())) return;
    setBusy('issue');
    try {
      setLetter(await issueLetter(leadUuid));
      toast('success', t('Letter sent to your client'));
      onIssued?.();
    } catch (err) {
      toast('error', errorMessage(err, t('Could not issue the letter')));
    } finally {
      setBusy(null);
    }
  };

  const confirmIssue = () => dispatch(showModal({
    showModal: true,
    type: 'confirmation',
    title: t('Issue engagement letter'),
    message: t('Send the letter to your client?'),
    subText: t('It is saved in the matter and can no longer be edited. The client confirms it in their request.'),
    icon: <PiPaperPlaneTiltLight className="w-6 h-6 text-info-600" />,
    variant: 'info',
    confirmLabel: t('Issue'),
    onConfirm: issue,
  }));

  const confirm = async () => {
    setBusy('confirm');
    try {
      setLetter(await confirmLetter(leadUuid, name.trim()));
      toast('success', t('Engagement letter confirmed'));
    } catch (err) {
      toast('error', errorMessage(err, t('Could not confirm the letter')));
    } finally {
      setBusy(null);
    }
  };

  /** Off to BankID; the shared callback page brings the client back here. */
  const sign = async () => {
    setBusy('sign');
    try {
      const { authorize_url: url } = await startLetterSigning(leadUuid);
      try { sessionStorage.setItem(SIGNING_LEAD_KEY, leadUuid); } catch { /* only for a cancelled return */ }
      window.location.assign(url);
    } catch (err) {
      toast('error', errorMessage(err, t('Could not start BankID')));
      setBusy(null);
    }
  };

  if (letter === undefined) return <Skeleton variant="card" />;
  if (letter === null) return null;
  // Client, before the lawyer issued it.
  if (letter.status === 'pending') {
    return (
      <GlassCard icon={<PiSignatureLight />} title={t('Engagement letter')}>
        <p className="text-sm text-fg-muted" data-testid="marketplace-letter-pending">
          {t('Your lawyer will send you an engagement letter with the terms of the assignment.')}
        </p>
      </GlassCard>
    );
  }

  const clientConfirmation = letter.confirmations?.find((c) => c.party === 'client');

  return (
    <GlassCard icon={<PiSignatureLight />} title={t('Engagement letter')}
      subtitle={letter.status === 'draft'
        ? t('Drafted from the agreed terms. Fill in the marked parts, then issue it to your client.')
        : letter.status === 'issued'
          ? (as === 'client'
            ? (letter.sign_with === 'typed_name'
              ? t('From your lawyer. Read it and confirm it below.')
              : t('From your lawyer. Read it and sign it with BankID below.'))
            : letter.sign_with === 'typed_name'
              ? t('Issued {{date}}. Waiting for the client to confirm.', { date: fmtDate(letter.issued_at) })
              : t('Issued {{date}}. Waiting for the client to sign with BankID.', { date: fmtDate(letter.issued_at) }))
          : undefined}
      tone={letter.status === 'confirmed' ? 'success' : undefined}>
      <div className="space-y-3" data-testid={`marketplace-letter-${letter.status}`}>
        {letter.status === 'draft' ? (
          <>
            <Textarea id="marketplace-letter-text" label={t('Letter')} rows={18} value={text}
              onChange={(e) => setText(e.target.value)}
              className="font-mono text-[13px]"
              error={tooLong ? t('Max {{n}} characters', { n: MAX_LETTER }) : undefined} />
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className={`text-[11px] ${missing ? 'text-warning-700 dark:text-warning-300' : 'text-fg-muted'}`}
                data-testid="marketplace-letter-missing">
                {missing
                  ? t('{{n}} marked part(s) left to fill in', { n: missing })
                  : t('Ready to issue')}
              </span>
              <div className="flex gap-2">
                <Button variant="outline" size="small" onClick={save} isLoading={busy === 'save'}
                  disabled={busy !== null || !dirty || !text.trim() || tooLong}>
                  {t('Save draft')}
                </Button>
                <Button variant="primary" size="small" onClick={confirmIssue} isLoading={busy === 'issue'}
                  disabled={busy !== null || missing > 0 || !text.trim() || tooLong}
                  icon={<PiPaperPlaneTiltLight className="h-3.5 w-3.5" />}>
                  {t('Issue to client')}
                </Button>
              </div>
            </div>
          </>
        ) : (
          <Well className="max-h-[28rem] overflow-y-auto">
            <pre className="whitespace-pre-wrap break-words font-sans text-sm leading-relaxed text-fg-body">
              {letter.text}
            </pre>
          </Well>
        )}

        {letter.status === 'issued' && as === 'client' && letter.sign_with && letter.sign_with !== 'typed_name' && (
          <div className="space-y-2 border-t border-hairline-soft pt-3" data-testid="marketplace-letter-sign">
            <p className="text-[11px] text-fg-muted">
              {letter.sign_with === 'bankid_test'
                ? t('Test login: this environment has no real BankID. The signature is marked as a test.')
                : t('By signing you accept the terms in this letter. BankID confirms it is you; your name and the time are recorded.')}
            </p>
            <div className="flex justify-end">
              <Button variant="primary" size="small" onClick={sign} isLoading={busy === 'sign'} disabled={busy !== null}
                icon={<PiSignatureLight className="h-3.5 w-3.5" />}>
                {letter.sign_with === 'bankid_test' ? t('Sign with test login (not real BankID)') : t('Sign with BankID')}
              </Button>
            </div>
          </div>
        )}

        {letter.status === 'issued' && as === 'client' && letter.sign_with === 'typed_name' && (
          <div className="space-y-2 border-t border-hairline-soft pt-3" data-testid="marketplace-letter-confirm">
            <Input type="text" id="marketplace-letter-name" label={t('Your full name')} value={name}
              onChange={(e) => setName(e.target.value)} autoComplete="name" />
            <p className="text-[11px] text-fg-muted">
              {t('By confirming you accept the terms in this letter. Your name and the time are recorded.')}
            </p>
            <div className="flex justify-end">
              <Button variant="primary" size="small" onClick={confirm} isLoading={busy === 'confirm'}
                disabled={busy !== null || name.trim().length < 2}>
                {t('Confirm letter')}
              </Button>
            </div>
          </div>
        )}

        {letter.status === 'confirmed' && clientConfirmation && (
          <p className="flex items-center gap-1.5 text-[12px] text-success-700 dark:text-success-300">
            <PiCheckCircleLight className="h-4 w-4 shrink-0" />
            {{
              bankid: t('Signed with BankID by {{name}} on {{date}}', { name: clientConfirmation.name, date: fmtDate(clientConfirmation.at) }),
              bankid_test: t('Signed with a test login (not real BankID) by {{name}} on {{date}}', { name: clientConfirmation.name, date: fmtDate(clientConfirmation.at) }),
              typed_name: t('Confirmed by {{name}} on {{date}}', { name: clientConfirmation.name, date: fmtDate(clientConfirmation.at) }),
            }[clientConfirmation.method] ?? t('Confirmed by {{name}} on {{date}}', { name: clientConfirmation.name, date: fmtDate(clientConfirmation.at) })}
          </p>
        )}
      </div>
    </GlassCard>
  );
};

export default EngagementLetter;
